import { Injectable, inject, signal, NgZone } from '@angular/core';
import { publishNotificationSession } from 'sham-components/services/notification-session';
import { SigninApi } from './signin-api';
import {
  catchError,
  finalize,
  of,
  tap,
  Observable,
  shareReplay,
  Subject,
  takeUntil,
  throwError,
} from 'rxjs';
import { Router } from '@angular/router';
import { LoginResponseDto, UserDto } from 'sham-components/shared/models/auth';
import { HttpClient } from '@angular/common/http';
import { API_BASE_URL } from '@/environments/environment';
import {
  clearDashboardSessionStorage,
  verifyDashboardSession,
} from 'sham-components/services';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly signinApi = inject(SigninApi);
  private readonly zone = inject(NgZone);
  private readonly router = inject(Router);
  private readonly http = inject(HttpClient);
  private readonly isBrowser = typeof window !== 'undefined';

  readonly isLoggedIn = signal<boolean>(false);
  private refreshTimeoutId: any = null;
  private refreshRequest$: Observable<LoginResponseDto> | null = null;
  private readonly logout$ = new Subject<void>();

  processRefreshVerification(): Observable<any> {
    if (!this.isBrowser) return of(null);
    return verifyDashboardSession({
      refresh: () => this.refreshToken(),
      onValidAccess: () => {
        this.isLoggedIn.set(true);
        this.queueSilentRefresh();
      },
      clear: () => this.clearSession(),
    });
  }

  saveLogin(response: LoginResponseDto, rememberMe: boolean) {
    if (!this.isBrowser) return;

    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem('accessToken', response.accessToken);
      sessionStorage.setItem('user', JSON.stringify(response.user));
      localStorage.setItem('seller_id', response.user.userId);

      sessionStorage.setItem(
        'accessTokenExpiresAt',
        response.accessTokenExpiresAtUtc,
      );
    }

    localStorage.removeItem('refreshToken');
    sessionStorage.removeItem('refreshToken');
    if (response.refreshToken) {
      (rememberMe ? localStorage : sessionStorage).setItem(
        'refreshToken',
        response.refreshToken,
      );
    }

    this.isLoggedIn.set(true);
    this.queueSilentRefresh();
  }

  private queueSilentRefresh() {
    publishNotificationSession();
    if (!this.isBrowser || typeof sessionStorage === 'undefined') return;

    if (this.refreshTimeoutId) {
      clearTimeout(this.refreshTimeoutId);
    }

    const expiresAtStr = sessionStorage.getItem('accessTokenExpiresAt');
    const refreshToken = this.getRefreshToken();

    if (!expiresAtStr || !refreshToken) return;

    const expiryTime = new Date(expiresAtStr).getTime();
    const now = new Date();
    const currentUtcTime = Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      now.getUTCHours(),
      now.getUTCMinutes(),
      now.getUTCSeconds(),
      now.getUTCMilliseconds(),
    );

    const timeRemaining = expiryTime - currentUtcTime;
    const bufferTime = 2 * 60 * 1000;
    const delay = timeRemaining - bufferTime;

    if (delay <= 0) {
      this.triggerSilentRefreshRequest();
    } else {
      this.zone.runOutsideAngular(() => {
        this.refreshTimeoutId = setTimeout(() => {
          this.zone.run(() => {
            this.triggerSilentRefreshRequest();
          });
        }, delay);
      });
    }
  }

  private triggerSilentRefreshRequest() {
    this.refreshToken().subscribe({ error: () => undefined });
  }

  getUser(): UserDto | null {
    if (!this.isBrowser || typeof sessionStorage === 'undefined') return null;
    const userStr = sessionStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  logout() {
    this.logout$.next();
    this.clearSession();
    void this.router.navigate(['/login']);
  }

  private clearSession() {
    if (!this.isBrowser) return;
    if (this.refreshTimeoutId) clearTimeout(this.refreshTimeoutId);
    clearDashboardSessionStorage();
    localStorage.removeItem('seller_id');
    this.isLoggedIn.set(false);
  }

  handleLoginRedirection(user: UserDto) {
    if (!user || !user.roles) {
      this.router.navigate(['/login']);
      return;
    }

    // التحقق من الرول (Role) وتوجيه المستخدم بناءً عليه
    if (user.roles.includes('Seller')) {
      // توجيه السيلر إلى لوحة التحكم الخاصة به
      this.router.navigate(['/app']);
    } else if (user.roles.includes('Customer')) {
      // توجيه الكستمر إلى صفحة إكمال البيانات الشخصية
      this.router.navigate(['/signup']);
    } else {
      // في حال كان الرول غير معرف أو افتراضي
      this.router.navigate(['/login']);
    }
  }
  //  handleLoginRedirection(user: UserDto) {

  //       this.router.navigate(['/app']);
  //   }
  isSeller(): boolean {
    const user = this.getUser();
    return user?.roles.includes('Seller') ?? false;
  }

  isCustomer(): boolean {
    const user = this.getUser();
    return user?.roles.includes('Customer') ?? false;
  }
  // أضف هذه الدالة إلى AuthService
  refreshToken(): Observable<LoginResponseDto> {
    if (this.refreshRequest$) {
      return this.refreshRequest$;
    }

    const refreshToken = this.getRefreshToken();

    if (!refreshToken) {
      this.logout();
      return throwError(() => new Error('No refresh token'));
    }

    const rememberMe = localStorage.getItem('refreshToken') !== null;
    this.refreshRequest$ = this.signinApi.refreshToken({ refreshToken }).pipe(
      takeUntil(this.logout$),
      tap((response) => {
        this.saveLogin(response, rememberMe);
      }),
      catchError((err) => {
        this.clearSession();
        return throwError(() => err);
      }),
      finalize(() => (this.refreshRequest$ = null)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    return this.refreshRequest$;
  }

  private getRefreshToken(): string | null {
    return (
      localStorage.getItem('refreshToken') ?? sessionStorage.getItem('refreshToken')
    );
  }

  getMe(): Observable<AccountSummary> {
    return this.http.get<AccountSummary>(`${API_BASE_URL}/api/v1/account/me`);
  }
}

export interface AccountSummary {
  userId: string;
  fullName: string | null;
  userName: string;
  email: string;
  joinDateUtc: string;
  profilePhotoUrl: string | null;
  roles: string[];
}
